import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Section, SectionHead } from "@/components/sn/SectionHead";

export const faqs = [
  {
    q: "What is Alpha with AI Club Premium?",
    a: "It is an ongoing membership that gives you access to our complete library of reproducible AI research playbooks, live workflow recordings, weekly drops, member-only masterclasses, and a private analyst club.",
    href: "/ai-investing-club",
    proof: "The Club page",
  },
  {
    q: "What is included with my membership?",
    a: "Every playbook on Alpha with AI Club, the full archive of past masterclass recordings, all new playbooks released each week, and access to the members-only club channel with the founder. It also covers US and global equity research, with a dated model book alongside it.",
    href: "/ai-investing-club",
    proof: "The Club page",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Both monthly and annual plans can be cancelled at any time from your account. You keep access until the end of your current billing period and there are no cancellation fees.",
    href: "/dashboard",
    proof: "Your dashboard",
  },
  {
    q: "Do I get access to past masterclasses?",
    a: "Yes. Members get the full archive of past masterclass recordings, available on demand the moment you join.",
  },
  {
    q: "Is there a free way to try it first?",
    a: "Yes. Browse the free playbooks on the Alpha with AI Club page to see the format, then upgrade to membership when you want the full library.",
    href: "/ai-investing-club",
    proof: "The free playbooks",
  },
  {
    q: "Do I get the paid newsletter with my Club membership?",
    a: "Yes. Every active Alpha with AI Club premium member gets complimentary access to the paid Alpha with AI newsletter on Substack. Sign in on Substack with the same email you used here. If you ever hit a paywall, write to info@shikshannivesh.com and we will fix it right away.",
    href: "https://ai.shikshannivesh.com/",
    proof: "The newsletter",
  },
];

export const SN_MembershipFAQ = ({ label = "06 · Questions" }: { label?: string }) => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };

  return (
    <Section>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <SectionHead label={label} title="Frequently asked questions." />

      <div className="mt-10 max-w-[760px]">
        {faqs.map((f) => (
          <details key={f.q} className="group border-t border-[var(--sn-border)] py-4 [&_summary::-webkit-details-marker]:hidden">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-6">
              <h3 className="m-0 text-[16px] font-medium leading-[1.4] tracking-[-0.01em] text-foreground">{f.q}</h3>
              <span
                className="flex-shrink-0 font-mono text-[14px] text-muted-foreground transition-transform group-open:rotate-45"
                aria-hidden="true"
              >
                +
              </span>
            </summary>
            <p className="mt-3 max-w-[62ch] text-[14.5px] leading-[1.6] text-[var(--sn-fg2)]">{f.a}</p>
            {f.href && (
              f.href.startsWith("http") ? (
                <a
                  href={f.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-block text-[13.5px] font-medium text-foreground transition-colors hover:text-primary"
                >
                  {f.proof} &rarr;
                </a>
              ) : (
                <Link
                  to={f.href}
                  className="mt-2 inline-block text-[13.5px] font-medium text-foreground transition-colors hover:text-primary"
                >
                  {f.proof} &rarr;
                </Link>
              )
            )}
          </details>
        ))}
      </div>
    </Section>
  );
};
